/**
 * Укладка навигационных предупреждений на карту MapLibre.
 *
 * Слои — среди рубежей (`nw-` в `BOUNDARY_PREFIXES`): ложатся поверх растра,
 * а картинка, пришедшая позже, ложится под них сама (`addUnderBoundaries`).
 * Стрельбы залиты плотнее прочих: по ним капитан решает, куда не идти.
 */
import type maplibregl from 'maplibre-gl'

import {
  NAVWARN_EDGE_LAYER, NAVWARN_FILL_LAYER, NAVWARN_HIT_LAYERS, NAVWARN_LAYER_IDS,
  NAVWARN_LINE_LAYER, NAVWARN_POINT_LAYER, NAVWARN_SELECTED_LAYER, NAVWARN_SOURCE_ID,
  navwarnColorExpression,
} from './navwarn'

const FIRING_EXPR = ['in', ['get', 'kind'], ['literal', ['missile', 'rocket', 'gunnery', 'exercises']]]
const IS_AREA = ['in', ['geometry-type'], ['literal', ['Polygon', 'MultiPolygon']]]
const IS_LINE = ['in', ['geometry-type'], ['literal', ['LineString', 'MultiLineString']]]
const IS_POINT = ['==', ['geometry-type'], 'Point']

/** Фильтр выделения: все фигуры одного сообщения. */
export const selectedFilter = (id: number | null): unknown[] =>
  ['==', ['get', 'id'], id ?? -1]

export function applyNavwarnLayers(
  map: maplibregl.Map, data: GeoJSON.FeatureCollection, beforeId?: string,
): void {
  const existing = map.getSource(NAVWARN_SOURCE_ID) as maplibregl.GeoJSONSource | undefined
  if (existing) {
    existing.setData(data)
    return
  }
  map.addSource(NAVWARN_SOURCE_ID, { type: 'geojson', data })
  const color = navwarnColorExpression() as unknown as string
  const before = beforeId && map.getLayer(beforeId) ? beforeId : undefined
  map.addLayer({
    id: NAVWARN_FILL_LAYER, type: 'fill', source: NAVWARN_SOURCE_ID,
    filter: IS_AREA as never,
    paint: {
      'fill-color': color,
      'fill-opacity': ['case', FIRING_EXPR, 0.28, 0.14] as never,
    },
  }, before)
  map.addLayer({
    id: NAVWARN_EDGE_LAYER, type: 'line', source: NAVWARN_SOURCE_ID,
    filter: IS_AREA as never,
    paint: { 'line-color': color, 'line-width': 1.4, 'line-opacity': 0.95 },
  }, before)
  map.addLayer({
    id: NAVWARN_LINE_LAYER, type: 'line', source: NAVWARN_SOURCE_ID,
    filter: IS_LINE as never,
    paint: { 'line-color': color, 'line-width': 2.2, 'line-dasharray': [3, 1.5] },
  }, before)
  map.addLayer({
    id: NAVWARN_POINT_LAYER, type: 'circle', source: NAVWARN_SOURCE_ID,
    filter: IS_POINT as never,
    paint: {
      'circle-radius': 5, 'circle-color': color,
      'circle-stroke-width': 1.5, 'circle-stroke-color': '#ffffff',
    },
  }, before)
  map.addLayer({
    id: NAVWARN_SELECTED_LAYER, type: 'line', source: NAVWARN_SOURCE_ID,
    filter: ['all', ['!', IS_POINT], selectedFilter(null)] as never,
    paint: { 'line-color': '#ffffff', 'line-width': 3.2, 'line-opacity': 0.95 },
  }, before)
}

/** Выделить фигуры сообщения; `null` — снять выделение. */
export function selectNavwarn(map: maplibregl.Map, id: number | null): void {
  if (!map.getLayer(NAVWARN_SELECTED_LAYER)) return
  map.setFilter(NAVWARN_SELECTED_LAYER, ['all', ['!', IS_POINT], selectedFilter(id)] as never)
}

export function removeNavwarnLayers(map: maplibregl.Map): void {
  for (const id of [...NAVWARN_LAYER_IDS].reverse()) {
    if (map.getLayer(id)) map.removeLayer(id)
  }
  if (map.getSource(NAVWARN_SOURCE_ID)) map.removeSource(NAVWARN_SOURCE_ID)
}

/** Сообщение под точкой клика: id и ключ (сводка, год, номер). */
export function navwarnAt(
  map: maplibregl.Map, point: maplibregl.PointLike,
): { id: number; navarea: string; year: number; number: number } | null {
  const layers = NAVWARN_HIT_LAYERS.filter(id => map.getLayer(id))
  if (!layers.length) return null
  const p = map.queryRenderedFeatures(point, { layers })[0]?.properties
  if (!p) return null
  return { id: Number(p.id), navarea: String(p.navarea), year: Number(p.year), number: Number(p.number) }
}
